import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { TESTIMONIALS, GOOGLE_RATING } from "../mock";
import Seo from "../components/Seo";

const Reviews = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.1 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="pt-24">
      <Seo
        title="Reviews — Alameda Brazilian Jiu-Jitsu & Self-Defense Academy"
        description="Read genuine reviews from Alameda BJJ families and students. See why parents, kids and adults across the East Bay train with Professor Sergio Silva."
        path="/reviews"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Reviews", path: "/reviews" }]}
      />
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Reviews</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">
            What Our Community Says
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-4 py-2 shadow-sm">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, s) => <Star key={s} size={17} className="fill-br-yellow text-br-yellow" />)}
              </div>
              <span className="text-sm font-semibold text-[#1A1A1A]">{GOOGLE_RATING.stars}.0</span>
              <span className="text-sm text-neutral-500">· {GOOGLE_RATING.count} Google reviews</span>
            </div>
            <a href="https://www.google.com/maps/search/Alameda+Brazilian+Jiu-Jitsu+%26+Self-Defense+Academy" target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full border-neutral-300 font-semibold h-10">
                View on Google <ArrowUpRight size={15} className="ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="reveal mb-6 break-inside-avoid bg-white rounded-2xl border border-neutral-100 p-7 card-hover">
                <div className="flex items-center justify-between">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, s) => <Star key={s} size={15} className="fill-br-yellow text-br-yellow" />)}
                  </div>
                  <Quote className="text-neutral-200" size={26} />
                </div>
                <p className="mt-4 text-neutral-700 leading-relaxed">“{t.quote}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-br-green/10 text-br-green flex items-center justify-center font-display font-bold">
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] text-sm">{t.name}</p>
                    <p className="text-xs text-neutral-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="reveal mt-16 rounded-3xl bg-br-green px-8 py-14 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 h-40 w-40 bg-br-yellow/20 rounded-full blur-3xl" />
            <h2 className="font-display text-3xl font-extrabold text-white relative">Come See Why Families Love It Here</h2>
            <p className="mt-4 text-green-50 max-w-xl mx-auto relative">Your first class is on us — no experience needed, all ages welcome.</p>
            <div className="mt-8 relative">
              <Link to="/trial"><Button className="h-12 px-8 bg-white text-br-green hover:bg-neutral-100 rounded-full font-semibold">Book a Free Trial</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
